/**
 * ชนิดข้อมูลของ Executive Dashboard (P2-10d) — เขียนตาม payload contract ที่ freeze
 * แล้วใน `apps/api/src/cap_erp_api/modules/reporting/{router,schemas,metrics}.py`
 * (D-106, `docs/SECURITY.md` — ข้อยกเว้นข้อแรกของ D-004 ที่ตั้งใจ)
 *
 * **จงใจไม่ import อะไรข้ามกลุ่ม** เหมือนทุก `lib/**` อื่นในโปรเจกต์นี้: `EXECUTIVE_DASHBOARD`
 * เป็นโมดูลสิทธิ์ของตัวเอง คนละชุดกฎธุรกิจ และคนละหน่วยงานกับทุกกลุ่มที่มีอยู่ก่อน
 *
 * ⚠️ **ห้ามใส่รายการสิบ metric code เป็น literal ที่นี่** — invariant ของ Work Order
 * นี้เขียนไว้ตรง ๆ ว่านี่คือ drift ที่โปรเจกต์นี้เคยโดนมาแล้วสองครั้ง `metric_code`
 * และ `metric_unit` ของแต่ละแถวมาจาก payload ที่เซิร์ฟเวอร์ตอบเสมอ (ดู
 * `parseExecutiveSummaryItem` ใน `client.ts`) หน้าจอ**อ่าน**หน่วยจากแถว ไม่เคย
 * map จากรหัส metric เอง
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — ตรงกับ `EXECUTIVE_DASHBOARD_MODULE` ฝั่งเซิร์ฟเวอร์ */
export const EXECUTIVE_DASHBOARD_MODULE = 'EXECUTIVE_DASHBOARD';

/**
 * สิทธิ์เดียวที่โมดูลนี้เคยผูก — ไม่มี CREATE/EDIT/APPROVE เพราะตารางนี้ไม่มี
 * เส้นทางเขียนเลย (D-106 ข้อ 5) — `router.py` ประกาศไว้ว่านี่คือการตัดสินใจ
 * ไม่ใช่ของที่ยังไม่ได้ทำ
 */
export const EXECUTIVE_DASHBOARD_ACTION = 'VIEW';

/**
 * `scope` ที่ contract รับ — **ไม่มีค่า default** ทั้งฝั่งเซิร์ฟเวอร์และที่นี่
 * `PLANT` คือแถวแยกรายโรงงาน (`plant_id IS NOT NULL`) และ `CONSOLIDATED` คือ
 * ยอดรวมทั้งบริษัทที่ fold ไว้ตั้งแต่ write time (`plant_id IS NULL`) — สองคำตอบนี้
 * เป็นคนละเลขกันแม้จะมีรูปเดียวกัน ผู้เรียกจึงต้องระบุเสมอ
 */
export const SNAPSHOT_SCOPES = ['PLANT', 'CONSOLIDATED'] as const;
export type SnapshotScope = (typeof SNAPSHOT_SCOPES)[number];

export function isSnapshotScope(value: string): value is SnapshotScope {
  return (SNAPSHOT_SCOPES as readonly string[]).includes(value);
}

/**
 * `metric_unit` เท่าที่ `MetricUnit` ฝั่งเซิร์ฟเวอร์ (`modules/reporting/metrics.py`)
 * ประกาศไว้ — ใช้ตรวจรูปร่างตอน parse เท่านั้น **ไม่ใช่ตารางที่ map จาก metric code**
 * หน่วยของแต่ละแถวมาจาก `metric_unit` ในแถวนั้นเองเสมอ
 */
export const METRIC_UNITS = ['QTY', 'AMOUNT', 'COUNT'] as const;
export type MetricUnit = (typeof METRIC_UNITS)[number];

export function isMetricUnit(value: string): value is MetricUnit {
  return (METRIC_UNITS as readonly string[]).includes(value);
}

/**
 * หนึ่งแถวของ `executive_summary_snapshot` — ตรงกับ `ExecutiveSummaryItemResponse`
 *
 * `plant_id` เป็น `string | null` — `null` **แปลว่ายอดรวมทั้งบริษัท** ไม่ใช่ "ไม่มี
 * โรงงาน" (`docs/SECURITY.md`) และเป็น id เดียวที่ payload นี้พกมา: ไม่มี `so_id`,
 * `delivery_id`, `doc_id`, `lot_id` หรือแม้แต่ `snapshot_id` — ห้ามเพิ่มคีย์ที่คาดว่า
 * จะมี เพราะ contract ประกาศไว้ว่านี่คือขอบเขตของข้อยกเว้นนี้โดยตั้งใจ
 *
 * `metric_value` เป็นสตริงเสมอ — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double
 * เก็บได้ครบ และเซิร์ฟเวอร์ render ที่ scale คงที่แล้ว (`render_value` ใน
 * `schemas.py`) ห้ามแปลงผ่าน `Number`/`parseFloat` ที่นี่
 */
export interface ExecutiveSummaryItem {
  readonly plant_id: string | null;
  readonly metric_code: string;
  readonly metric_unit: MetricUnit;
  readonly metric_value: string;
  /** `YYYY-MM-DD` ล้วน ไม่มีเวลาและไม่มี offset */
  readonly period_start: string;
  readonly period_end: string;
}

/** `GET /v1/executive-summary` — envelope มาตรฐานของทุกกลุ่ม */
export interface ExecutiveSummaryListPage {
  readonly items: readonly ExecutiveSummaryItem[];
  readonly next_cursor: string | null;
}

/* ------------------------------------------------------- กฎระดับคำขอ (422) -- */

/** `date_from` มาหลัง `date_to` — ช่วงว่าง */
export const EXECUTIVE_SUMMARY_RANGE_IS_REVERSED = 'EXECUTIVE_SUMMARY_RANGE_IS_REVERSED';
/** ช่วงกว้างเกิน `MAX_RANGE_DAYS` */
export const EXECUTIVE_SUMMARY_RANGE_TOO_WIDE = 'EXECUTIVE_SUMMARY_RANGE_TOO_WIDE';
/** `metric_code` ที่ระบุไม่อยู่ในวงคำศัพท์ที่ประกาศไว้ */
export const EXECUTIVE_SUMMARY_UNKNOWN_METRIC = 'EXECUTIVE_SUMMARY_UNKNOWN_METRIC';

/**
 * ช่วงวันที่กว้างที่สุดที่เส้นนี้จะตอบใน request เดียว — ตัวเลขเดียวกับ
 * `service.MAX_RANGE_DAYS` ฝั่งเซิร์ฟเวอร์ (365 วันปกติ, 366 ปีอธิกสุรทิน) mirror
 * มาเพื่อใช้เป็นขอบเขตของช่องเลือกวันที่บนหน้าจอ (invariant: "respect that bound in
 * the control instead of letting the user discover it as an error") — ไม่ใช่ตัวเลข
 * ที่หน้าจอเดาเอง เซิร์ฟเวอร์ยังเป็นผู้ตัดสินจริงทุกคำขอ
 */
export const EXECUTIVE_SUMMARY_MAX_RANGE_DAYS = 366;

/** จำนวนแถวต่อหน้าเริ่มต้น/สูงสุด — ตัวเลขเดียวกับ `service.py` */
export const EXECUTIVE_SUMMARY_DEFAULT_PAGE_LIMIT = 50;
export const EXECUTIVE_SUMMARY_MAX_PAGE_LIMIT = 200;
